import { getFormErrorMessage } from "@core/utility/get-error-message";
import { QuoteActivityFormRule, QuoteActivityFormSchema } from "@factory/form/form.rule";
import { useFormDefaultQuoteActivity } from "@factory/form/form-default-quote-activity.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseQuoteActivityModel } from "@factory/model/query-model-quote-activity";
import { 
  useQyDeleteQuoteActivity, 
  useQyGetQuoteActivityById, 
  useQyUpdateQuoteActivity} from "@factory/query/quote-activity.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditQuoteActivity() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultQuoteActivity();
  const navigate = useNavigate();
  const { mapUpdateQuoteActivity, mapDeleteQuoteActivity } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { quoteActivityId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID QuoteActivity
  const handleGetApiSuccess = (data: GetResponseQuoteActivityModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("baseActivity", responseData?.base_activity || "");
      setValue("quotation", responseData?.quotation || "");
      setValue("isModified", Boolean(responseData?.is_modified));
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
      setValue("day", responseData?.day);
      setValue("isChildActivity", Boolean(responseData?.is_child_activity));
      setValue("childRate", responseData?.child_rate);
      setValue("showAsOptional", Boolean(responseData?.show_as_optional));
      setValue("isFree", Boolean(responseData?.is_free));
      setValue("entranceFee", responseData?.entrance_fee);
      setValue("numPeople", responseData?.num_people);
      setValue("unit", responseData?.unit || "");
  };
  const {
    data: quoteActivityResponse,
    isLoading,
    isError: quoteActivityError,
  } = useQyGetQuoteActivityById(quoteActivityId || "", handleGetApiSuccess);
  const quoteActivityData = quoteActivityResponse?.data?.[0];

  // API UPDATE QuoteActivity
  const handleUpdateApiSuccess = () => {
    showSuccess("QuoteActivity updated successfully");
  };
  const { mutate: updateQuoteActivity, isLoading: isEditLoading } = useQyUpdateQuoteActivity(
    handleUpdateApiSuccess
  );

  // API DELETE QuoteActivity
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("QuoteActivity deleted successfully");
  };
  const { mutate: deleteQuoteActivity } = useQyDeleteQuoteActivity(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<QuoteActivityFormSchema>({
    defaultValues: getDefault(quoteActivityData),
    resolver: zodResolver(QuoteActivityFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: QuoteActivityFormSchema) => {
    if (!quoteActivityData || !quoteActivityData.code) {
      throw new Error("No quoteActivity data");
    }

    const formData = mapUpdateQuoteActivity(form, quoteActivityData.code);
    updateQuoteActivity(formData);
  };
  const handleValidateError = (err: FieldErrors<QuoteActivityFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!quoteActivityData || !quoteActivityData.code) {
      throw new Error("No quoteActivity data found");
    }

    const form = mapDeleteQuoteActivity(quoteActivityData.code);
    deleteQuoteActivity(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    quoteActivityId,
    quoteActivityData,
    isLoading,
    quoteActivityError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}